/**
 * Credits, in the header, because it is the figure checked most and it was at
 * the bottom of the page.
 *
 * ## WHY THIS REPLACED A CARD
 *
 * `wend-designer` measured the Overview at 1,280 × 800 against the live
 * workspace's own shape: the page is 5.84 viewports, and the credit balance sits
 * 3.37 viewports and 1,128 words down it. It is the one number a sender checks
 * *before* every campaign — "can I afford to send this" — and it was below every
 * explanation card on the screen. The owner's words were the measurement's:
 * *"to see credits I am scrolling to almost last of the page; credits should be
 * a chip kind of thing somewhere on the top."*
 *
 * **Nothing is deleted by the move, and this is the part worth checking rather
 * than trusting.** `CreditsCard` printed five figures — available, the messages
 * that buys, the balance, what is held in flight, and the ratio between credits
 * and messages. `/credits` already renders all five, per channel, from the same
 * `overviewChannelCredits()` builder this chip reads (`CreditsCells.tsx`,
 * `CreditsChannelBalance`) — plus one state the Overview never carried at all:
 * a workspace no credit has ever reached, which `/credits` says outright is an
 * absence rather than a balance of zero. The chip is a link to that screen, so
 * the move is from *two places* to one, and the one is the better one.
 *
 * ## A ZERO HERE IS A REAL ZERO
 *
 * Everywhere else on this dashboard a `0` is the defect — a rate over mail
 * nobody reported on, a bounce count that is a count of reports that never
 * arrived. Credits are the exception and the contract says so in
 * `overviewChannelCredits`: *"A zero row is a real answer and must render as
 * zero rather than as absence."* A workspace that has spent everything has
 * nought available, and that is exactly the thing this chip exists to say. So
 * there is no `NoFigure` here and there must not be one: the absence state —
 * no credit has *ever* arrived — is not on this response, it is on
 * `CreditBalance.updatedAt`, and it is drawn on the screen that carries it.
 *
 * ## WHAT IT DOES NOT DO
 *
 * **No threshold, no colour, no warning.** "Low" would be a number this product
 * does not have: what counts as low depends on the size of the next campaign,
 * which this component cannot see. A warn tint on a figure nobody set a
 * threshold for is an invented verdict wearing the same paint as the two real
 * ones on the tiles below, and those two — bounce and complaint — are the ones
 * that can cost somebody their AWS account. It stays neutral.
 *
 * **It never says spend, cost, charge or price.** `./credit.js`'s vocabulary
 * rule, restated because this is a screen: a credit is Wend's metering unit and
 * has no exchange rate with anybody's AWS invoice.
 */
import type { OverviewChannelCredits } from '@wend/shared';
import { cn, focusRing } from '@wend/ui';
import { Link } from 'react-router-dom';

import { CHANNEL_LABEL } from '../../lib/campaign-display';
import { formatCount, pluralise } from '../../lib/format';

export function CreditsChip({ credits }: { credits: readonly OverviewChannelCredits[] }) {
  if (credits.length === 0) return null;

  /*
   * The accessible name, built once and in full. Visually this is three or four
   * tokens separated by a middot, which a screen reader reads as a run of bare
   * numbers — "8,884 email 1,000 WhatsApp" with no noun anywhere. The link's
   * name says what they are and what they buy, which is the sentence the card
   * this replaced had room for.
   */
  const spoken = credits
    .map(
      (entry) =>
        `${pluralise(entry.available, `${CHANNEL_LABEL[entry.channel]} credit`)} available, about ${pluralise(entry.messagesRemaining, 'more message')}`,
    )
    .join('; ');

  return (
    <Link
      to="/credits"
      aria-label={`Credits: ${spoken}. Open the credits screen.`}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border border-line-strong bg-white/[0.04] px-3 py-1',
        'text-muted transition-colors hover:border-line-strong hover:bg-white/[0.07] hover:text-text',
        focusRing,
      )}
    >
      {/*
        THE NOUN, WHICH THE COIN WAS STANDING IN FOR.

        This opened with a lucide coin and no word, so a sighted reader met a run
        of bare numbers in the header and had to infer what they counted from a
        16px glyph. The approved design says `Credits 8,420` — the word, then the
        figure in mono. The icon went with the word arriving: two carriers of the
        same fact in a 14px-tall bar is one too many, and the one that survives
        should be the one that can be read aloud.

        Still `aria-hidden` as a unit, because the link's own name already says
        every figure in words; leaving it readable makes a screen reader say each
        one twice, the second time without its noun.
      */}
      <span aria-hidden="true" className="flex items-center gap-2">
        <span className="text-[0.78rem]">Credits</span>
        {credits.map((entry, index) => (
          <span key={entry.channel} className="flex items-center gap-1.5">
            {index > 0 && <span className="text-muted/50">·</span>}
            <span className="font-mono text-[0.78rem] font-semibold tabular-nums text-text">
              {formatCount(entry.available)}
            </span>
            <span className="text-[0.7rem] text-muted">{CHANNEL_LABEL[entry.channel]}</span>
          </span>
        ))}
      </span>
    </Link>
  );
}
